<script setup lang="ts">
type Props = {
  notification: TInboxMessage;
};

const { notification } = defineProps<Props>();

const { runAction } = useSmarticoInit();
const { openChangeNickname, openChangeAvatar } = useSmarticoUserInfos();
const { loadSingleMessage, markNotificationAsRead, loadMessages } =
  useSmarticoMessages();

const isReadManual = ref(false);
const isRead = computed(() => notification.read || isReadManual.value);

// Favoritar é só visual por enquanto: o SDK do Smartico não expõe um método de
// persistência no nosso wrapper. TODO: ligar à API quando disponível.
const isFavorite = ref(notification.favorite);

const body = ref<TInboxMessageBody>();

async function loadDetails() {
  body.value = await loadSingleMessage(notification.message_guid);
}

// Parser que descarta o que não é conteúdo visível (o <style> que o marketing
// embute no título, scripts, etc.).
function cleanDoc(html?: string): HTMLDivElement {
  const container = document.createElement('div');

  container.innerHTML = html || '';

  container
    .querySelectorAll('style, script, noscript, head, title, meta, link')
    .forEach((el) => el.remove());

  return container;
}

function plainText(node: HTMLElement): string {
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
}

// O Smartico destaca a 1ª linha (<span class="toptext1">) como label colorida;
// o restante do título é o título de fato.
const label = computed(() =>
  plainText(
    (cleanDoc(body.value?.title).querySelector('.toptext1') as HTMLElement) ||
      document.createElement('span'),
  ),
);

const title = computed(() => {
  const doc = cleanDoc(body.value?.title);

  doc.querySelector('.toptext1')?.remove();

  return plainText(doc);
});

const text = computed(() =>
  plainText(
    cleanDoc(
      body.value?.preview_body || body.value?.body || body.value?.html_body,
    ),
  ),
);

const image = computed(
  () =>
    body.value?.image ||
    cleanDoc(body.value?.html_body || body.value?.body)
      .querySelector('img')
      ?.getAttribute('src') ||
    '',
);

// CTA do card: o botão cuja action não é um comando dp:* (ex.: link da loja).
// Cai pra action do card se ela também for navegável.
const ctaAction = computed(() => {
  const fromButtons = (body.value?.additional_buttons || []).find(
    (btn) => btn.action && !btn.action.startsWith('dp:'),
  )?.action;

  const cardAction = body.value?.action;

  return (
    fromButtons ||
    (cardAction && !cardAction.startsWith('dp:') ? cardAction : '')
  );
});

// "há um dia", "há 3 h", etc. a partir de "YYYY-MM-DD HH:MM:SS".
const relativeDate = computed(() => {
  const raw = notification.sent_date?.replace(' ', 'T');

  if (!raw) return '';

  const then = new Date(raw).getTime();

  if (isNaN(then)) return notification.sent_date;

  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);

  if (day >= 1) return day === 1 ? 'há um dia' : `há ${day} dias`;
  if (hour >= 1) return `há ${hour} h`;
  if (min >= 1) return `há ${min} min`;

  return 'agora';
});

// Roteia a action pro destino certo. URLs vão pro navigateTo (não dependem do
// SDK), rotas internas pro /vip, nickname/avatar abrem os modais, resto runAction.
const INTERNAL_KEYS = ['missions', 'levels', 'tournaments', 'store'];

function dispatchAction(action: string) {
  if (action.startsWith('dp:go&url=')) {
    const url = action.split('dp:go&url=')[1];

    if (url) navigateTo(url, { open: { target: '_blank' } });

    return;
  }

  if (/^https:\/\//.test(action)) {
    navigateTo(action, { open: { target: '_blank' } });

    return;
  }

  const internal = INTERNAL_KEYS.find((key) => action.includes(key));

  if (internal) {
    navigateTo(localeLink(`/vip/${internal}`), { open: { target: '_blank' } });

    return;
  }

  if (action.includes('nickname')) return openChangeNickname();
  if (action.includes('avatar')) return openChangeAvatar();

  runAction(action);
}

async function markAsRead() {
  if (!notification.read) {
    await markNotificationAsRead(notification.message_guid);
    isReadManual.value = true;
  }
}

// Clique no card: só age se houver CTA navegável. Sem CTA, não marca lida nem
// esmaece (evita "ficar cinza" à toa).
function onCardClick() {
  if (!ctaAction.value) return;

  markAsRead();
  dispatchAction(ctaAction.value);
  loadMessages();
}

function toggleFavorite() {
  isFavorite.value = !isFavorite.value;
}

onMounted(loadDetails);
</script>

<template>
  <div
    class="gni"
    :class="{ 'is-read': isRead }"
  >
    <button
      class="gni-main"
      type="button"
      @click="onCardClick"
    >
      <div
        v-if="body"
        class="gni-thumb"
      >
        <span
          v-if="image"
          class="gni-img"
          :style="{ backgroundImage: `url('${image}')` }"
        ></span>
        <NuxtIcon
          v-else
          name="crown"
        />
      </div>

      <div class="gni-content">
        <span
          v-if="label"
          class="gni-label"
          >{{ label }}</span
        >
        <p class="gni-title">{{ title }}</p>
        <p
          v-if="text"
          class="gni-text"
        >
          {{ text }}
        </p>
      </div>
    </button>

    <div class="gni-side">
      <span class="gni-date">{{ relativeDate }}</span>

      <button
        aria-label="Favoritar"
        class="gni-fav"
        :class="{ 'is-fav': isFavorite }"
        type="button"
        @click="toggleFavorite"
      >
        <NuxtIcon name="home/favorite" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gni {
  display: flex;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gni-main {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  background: none;
}

.gni-thumb {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 10px;

  // Imagem via background (div não tem tamanho intrínseco) — fica sempre 44px,
  // sem o gotcha de flex min-width:auto que estourava a <img>.
  .gni-img {
    width: 100%;
    height: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
  }

  :deep(svg) {
    width: 26px;
    height: 26px;
    color: #ffd700;
  }
}

.gni-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.gni-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  // Gradiente animado (mesmo do .toptext1 do Smartico). Usa animation, não
  // transition, por causa do reset global em main.scss.
  background: linear-gradient(
    90deg,
    #00d4ff,
    #ff9800,
    #f44336,
    #9c27b0,
    #673ab7,
    #00d4ff
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gni-rainbow 10s linear infinite;
}

@keyframes gni-rainbow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

.gni-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
}

.gni-text {
  font-size: 12px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.5);
  // Limita a 2 linhas.
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gni-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
}

.gni-date {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}

.gni-fav {
  display: flex;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.25);

  &.is-fav {
    color: #e0457b;
  }

  :deep(svg) {
    width: 16px;
    height: 16px;
  }
}
</style>
